"use client"

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { CalendarDots as CalendarDays, Gauge, ArrowsClockwise as RefreshCw, HardDrives as Server, User as UserRound, Wrench } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"

interface UserAccount {
  id: number
  name: string
  account: string
  password: string
  freeze: number
  server: number
  taskType: string
  refresh: number
  agent: string | null
  createTime: string
  updateTime: string
  expireTime: string
  san: string
  config: any
  active: any
  notice: any
}

interface UserDetailDialogProps {
  user: UserAccount | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onResetRefresh?: (id: number) => void
  onResetSanity?: (id: number) => void
}

const dayNames: Record<string, string> = {
  monday: "周一", tuesday: "周二", wednesday: "周三", thursday: "周四",
  friday: "周五", saturday: "周六", sunday: "周日",
}

function formatDay(dateString: string) {
  const date = new Date(dateString)
  return Number.isNaN(date.getTime()) ? "未设置" : date.toLocaleDateString("zh-CN")
}

function getTaskTypeName(taskType: string) {
  const taskTypes: Record<string, string> = {
    daily: "日常任务", luxury: "豪华套餐", goods: "商品任务",
    rogue: "肉鸽·傀影与猩红孤钻", rogue2: "肉鸽·水月与深蓝之树",
    sami: "肉鸽·探索者的银凇止境", sarkaz: "肉鸽·萨卡兹的无终奇语",
    sui: "肉鸽·界园", blackflow: "肉鸽·黑流树海·寻森小队",
    sand_fire: "生息演算·沙洲遗闻", icebreaker: "生息演算·重启锚点",
  }
  return taskTypes[taskType] || taskType
}

function getActiveSummary(active: any) {
  const days = Object.entries(active || {}) as [string, { enable?: boolean; detail?: string[] }][]
  if (!days.length) return "未配置"
  const enabled = days.filter(([, value]) => value?.enable)
  if (enabled.length === days.length && enabled.every(([, value]) => !value?.detail?.length)) return "全天允许"
  return `允许 ${enabled.length}/${days.length} 天`
}

function isAllDayActive(entries: [string, { enable?: boolean; detail?: string[] }][]) {
  return entries.length > 0 && entries.every(([, value]) => value?.enable && !value?.detail?.length)
}

export function UserDetailDialog({ user, open, onOpenChange, onResetRefresh, onResetSanity }: UserDetailDialogProps) {
  if (!user) return null

  const expired = Boolean(user.expireTime) && new Date(user.expireTime) < new Date()
  const activeEntries = Object.entries(user.active || {}) as [string, { enable?: boolean; detail?: string[] }][]
  const allDayActive = isAllDayActive(activeEntries)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-user-edit admin-user-detail max-w-3xl">
        <DialogHeader className="admin-user-detail__header">
          <DialogTitle className="admin-user-detail__title"><UserRound aria-hidden="true" />{user.name}</DialogTitle>
          <DialogDescription>账号 {user.account} · {user.freeze ? "已冻结" : "正常使用中"}</DialogDescription>
        </DialogHeader>

        <div className="admin-user-detail__body">
          <section className="admin-user-detail__section" aria-labelledby="detail-basic">
            <h3 id="detail-basic">基本信息</h3>
            <dl className="admin-user-detail__grid">
              <div><dt>用户名</dt><dd>{user.name}</dd></div>
              <div><dt>账号</dt><dd>{user.account}</dd></div>
              <div><dt>服务器</dt><dd><Server aria-hidden="true" />{user.server === 0 ? "官服" : "B服"}</dd></div>
              <div><dt>任务类型</dt><dd>{getTaskTypeName(user.taskType)}</dd></div>
              <div><dt>状态</dt><dd><span className={`admin-status-pill ${user.freeze ? "admin-status-pill--frozen" : "admin-status-pill--normal"}`}>{user.freeze ? "已冻结" : "正常"}</span></dd></div>
              {user.agent && <div><dt>代理</dt><dd>{user.agent}</dd></div>}
            </dl>
          </section>

          <section className="admin-user-detail__section" aria-labelledby="detail-status">
            <h3 id="detail-status">到期与状态</h3>
            <dl className="admin-user-detail__grid">
              <div><dt>到期日期</dt><dd><CalendarDays aria-hidden="true" />{formatDay(user.expireTime)}<span className={`admin-status-pill ${expired ? "admin-status-pill--expired" : "admin-status-pill--normal"}`}>{expired ? "已到期" : "有效"}</span></dd></div>
              <div><dt>理智</dt><dd><Gauge aria-hidden="true" />{user.san || "—"}</dd></div>
              <div><dt>刷新次数</dt><dd><RefreshCw aria-hidden="true" />{user.refresh}</dd></div>
              <div><dt>创建日期</dt><dd>{formatDay(user.createTime)}</dd></div>
            </dl>
          </section>

          <section className="admin-user-detail__section admin-user-detail__active" aria-labelledby="detail-active">
            <h3 id="detail-active">活跃时间 <span>{getActiveSummary(user.active)}</span></h3>
            <div className="admin-user-detail__active-list">
              {allDayActive ? <p>周一至周日 · 全天允许</p> : activeEntries.length ? activeEntries.map(([day, config]) => (
                <div key={day}><strong>{dayNames[day] || day}</strong><span>{config?.enable ? (config.detail?.length ? config.detail.join("、") : "全天允许") : "当天不允许"}</span></div>
              )) : <p>未配置活跃时间。</p>}
            </div>
          </section>

          <section className="admin-user-detail__section admin-user-detail__maintenance" aria-labelledby="detail-maintenance">
            <h3 id="detail-maintenance"><Wrench aria-hidden="true" />维护操作</h3>
            <div className="admin-user-detail__maintenance-actions">
              <Button type="button" variant="outline" onClick={() => onResetRefresh?.(user.id)}>重置刷新次数</Button>
              <Button type="button" variant="outline" onClick={() => onResetSanity?.(user.id)}>重置理智</Button>
            </div>
          </section>
        </div>

      </DialogContent>
    </Dialog>
  )
}
